/**
 * Fetch-on-scroll pagination for the collection grid.
 *
 * Server paginates by 16 — each request returns exactly the next STEP of
 * cards. No prefetched/hidden cards in the DOM; every reveal is a network
 * request.
 *
 *   Desktop (≥750px), cycle = 2 scroll-fetches: initial 16 → scroll → scroll → button → scroll → scroll → button → …
 *   Mobile  (<750px), cycle = 2 scroll-fetches: identical to desktop.
 *
 * Modes (alternating):
 *   - Scroll mode (scrolls used in cycle < cycle quota): IntersectionObserver
 *     on #eg-infinite-pagination fires when the user reaches the bottom of
 *     the visible grid. The handler shows a loader and fetches
 *     `paginate.next.url`, appending the returned 16 cards.
 *   - Button mode (cycle quota reached, OR server is out of pages): observer
 *     disconnected, Load More button visible. Each click does the same
 *     fetch path with a button spinner.
 *   - Cycle reset: a successful button-click fetch resets the per-cycle
 *     scroll counter and re-arms the scroll observer with a "skip until
 *     exit" gate — the observer won't auto-fire until the user has
 *     scrolled the pagination element out of view at least once,
 *     preventing an already-visible pagination from instantly chaining
 *     into another fetch.
 *
 * Cycle quota is derived from the existing cap/step settings:
 * `(cap - step) / step`, so the current desktop (48/16=2) and mobile
 * (32/16=1) values keep their first-cycle behavior unchanged.
 *
 * Re-initialises after every facets AJAX (FacetFiltersForm
 * .renderProductGridContainer rewrites #ProductGridContainer.innerHTML;
 * we observe that container's subtree to catch each replacement of
 * #product-grid) and on `matchMedia('(max-width: 749px)') change` so
 * resize/rotate applies the correct cycle quota immediately.
 */
(function () {
  if (window.__egInfinitePaginationInit) return;
  window.__egInfinitePaginationInit = true;

  // Early scroll-restoration override for deep-load URLs. Runs at script
  // execution time — before DOMContentLoaded and before the browser's
  // auto-restoration kicks in (which races against our manual scrollTo
  // in `restoreScrollAfterRehydration` otherwise). Gated on
  // `?page > 1` so non-rehydrated routes (regular browsing, back from
  // PDP on a single-page collection) keep the browser-managed scroll-
  // restoration UX. The original value is captured here so rehydrate()
  // can put it back when it's done.
  var originalScrollRestoration = null;
  try {
    if ('scrollRestoration' in window.history) {
      originalScrollRestoration = window.history.scrollRestoration;
    }
    var earlyParams = new URLSearchParams(window.location.search);
    var earlyPage = parseInt(earlyParams.get('page'), 10);
    if (isFinite(earlyPage) && earlyPage > 1 && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  } catch (e) {}

  var DEFAULT_STEP = 16;
  var DEFAULT_CAP = 48;
  var DEFAULT_CAP_MOBILE = 48;
  var MOBILE_BP = '(max-width: 749px)';
  var COOLDOWN_MS = 350;

  function isMobile() {
    return window.matchMedia(MOBILE_BP).matches;
  }
  var SPINNER_HTML =
    '<div class="loading-overlay_spinner">' +
      '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
        '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
      '</svg>' +
    '</div>';

  // initToken bumps on every re-init so stale observer callbacks bail out
  // before mutating the new state.
  var initToken = 0;
  var scrollObserver = null;
  var cooldownUntil = 0;
  var isFetching = false;
  var savedButtonHTML = null;
  // Per-cycle scroll counter. Increments on each successful scroll-mode
  // fetch; once it hits the cycle quota the observer is disconnected and
  // the button is shown. Reset to 0 on init() and on each successful
  // button-click fetch — that's what produces the alternating
  // scroll/button rhythm.
  var scrollsThisCycle = 0;
  // True while we are mid-rehydration (parallel-fetching pages 1..N-1 on
  // a deep-load reload). The data-eg-rehydrating body attribute mirrors
  // this so analytics-collection.js can suppress its `load_more` fire-on-
  // grid-mutation logic during the prepend burst.
  var rehydrating = false;
  var SCROLL_STORAGE_KEY = 'arcline-eg-scroll';
  var SCROLL_STALE_MS = 10 * 60 * 1000;

  function getElements() {
    return {
      grid: document.querySelector('#product-grid'),
      pagination: document.querySelector('#eg-infinite-pagination')
    };
  }

  function getStep(pagination) {
    var v = parseInt(pagination.dataset.revealStep, 10);
    return v > 0 ? v : DEFAULT_STEP;
  }

  function getCap(pagination) {
    // Per-viewport cap: mobile breakpoint switches scroll-trigger off
    // earlier so phone users hit "Load More" sooner. Falls back to
    // `data-reveal-cap` if the per-viewport attrs aren't present
    // (e.g. main-search.liquid which doesn't pass them).
    var attr = isMobile() ? pagination.dataset.capMobile : pagination.dataset.capDesktop;
    var v = parseInt(attr, 10);
    if (v > 0) return v;
    v = parseInt(pagination.dataset.revealCap, 10);
    if (v > 0) return v;
    return isMobile() ? DEFAULT_CAP_MOBILE : DEFAULT_CAP;
  }

  // How many scroll-triggered fetches the user gets per cycle before the
  // button gates the next one. Derived from the existing cap/step so the
  // first cycle is identical to the previous single-shot behavior:
  // desktop (48,16)→2, mobile (32,16)→1. cap≤step means "no scrolls,
  // just button" — preserved for search where the cap math collapses.
  function getScrollsPerCycle(pagination) {
    var cap = getCap(pagination);
    var step = getStep(pagination);
    if (cap <= step || step <= 0) return 0;
    return Math.max(1, Math.floor((cap - step) / step));
  }

  function getTotal(pagination, grid) {
    var v = parseInt(pagination.dataset.total, 10);
    if (isFinite(v) && v > 0) return v;
    // Fallback when data-total is missing — count actual cards via the
    // same UL-aware path as countVisible (collection grid vs search
    // wrapper).
    return countVisible(grid);
  }

  // The element holding the actual <li> cards. On the COLLECTION page,
  // #product-grid IS the <ul>. On the SEARCH page, #product-grid is a
  // wrapper <div> that also contains the loading-overlay and an inner
  // #eg-infinite-pagination — the cards live in a child <ul> inside it.
  // Every "count" / "append" operation must target this inner element,
  // never #product-grid directly, or counts inflate by container children
  // and appends drag in duplicate pagination/loading nodes.
  function getCardsUl(grid) {
    if (!grid) return null;
    if (grid.tagName === 'UL') return grid;
    return grid.querySelector('ul');
  }

  function countVisible(grid) {
    var ul = getCardsUl(grid);
    return ul ? ul.children.length : 0;
  }

  function updateDisplay(pagination, grid, revealed, total) {
    var countEl = pagination.querySelector('.eg-pagination__count');
    if (countEl) {
      countEl.textContent = 'Showing ' + revealed + ' of ' + total + ' products';
    }
    var progress = pagination.querySelector('.eg-pagination__progress');
    var fill = pagination.querySelector('.eg-pagination__progress-fill');
    if (progress && fill) {
      var pct = total > 0 ? (revealed * 100 / total) : 100;
      progress.style.setProperty('--eg-progress', pct + '%');
      progress.setAttribute('aria-valuenow', Math.round(pct));
    }
    syncToolbar(revealed, total, getStep(pagination));
  }

  function syncToolbar(revealed, total, step) {
    var range = document.querySelector('.collection-toolbar__count-range');
    var totalEl = document.querySelector('.collection-toolbar__count-total');
    var pagesEl = document.querySelector('.collection-toolbar__pages');
    // "X of Y Pages" math has to follow the TOOLBAR's `data-page-size`, not
    // the pagination's reveal step. On the collection page they're both 16
    // (toolbar override), but on the search page the toolbar keeps its
    // server paginate size (e.g. 36) — writing reveal-step math there
    // would produce wrong page counts.
    var toolbar = document.querySelector('.collection-toolbar');
    var pageStep = toolbar ? parseInt(toolbar.getAttribute('data-page-size'), 10) : 0;
    if (!(pageStep > 0)) pageStep = step;
    if (range) {
      range.textContent = revealed === 0 ? 'Showing 0' : ('Showing 1-' + revealed);
    }
    if (totalEl) {
      totalEl.textContent = ' of ' + total + ' products';
    }
    if (pagesEl && pageStep > 0) {
      var totalP = Math.max(1, Math.ceil(total / pageStep));
      var curP = Math.min(totalP, Math.max(1, Math.ceil(revealed / pageStep)));
      pagesEl.textContent = curP + ' of ' + totalP + ' Pages';
    }
  }

  function disconnectObserver() {
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }
  }

  function showButton(pagination) {
    var btn = pagination.querySelector('.eg-pagination__button');
    if (btn) btn.hidden = false;
  }

  function hideButton(pagination) {
    var btn = pagination.querySelector('.eg-pagination__button');
    if (btn) btn.hidden = true;
  }

  function showLoader(pagination) {
    var loader = pagination.querySelector('.eg-pagination__loader');
    if (loader) loader.hidden = false;
  }

  function hideLoader(pagination) {
    var loader = pagination.querySelector('.eg-pagination__loader');
    if (loader) loader.hidden = true;
  }

  function attachScrollObserver(pagination, onReveal, skipUntilExit) {
    // `skipUntilExit`: when true (typically right after a button-click
    // fetch), the observer ignores intersection events until it first
    // sees the pagination leave the viewport. Without it, a Load More
    // click whose result still leaves the pagination on-screen would
    // immediately re-trigger another fetch — defeating the alternation.
    // The flag flips to "armed" the first time we see isIntersecting=false,
    // which is the natural side-effect of the user actually scrolling
    // (either past the pagination or back up to the new cards).
    disconnectObserver();
    var armed = !skipUntilExit;
    scrollObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) {
          armed = true;
          continue;
        }
        if (!armed) continue;
        if (Date.now() < cooldownUntil) return;
        cooldownUntil = Date.now() + COOLDOWN_MS;
        onReveal();
        return;
      }
    }, { rootMargin: '200px 0px' });
    scrollObserver.observe(pagination);
  }

  // Pure fetch-and-append. Network + DOM mutation only — UI affordances
  // (button spinner vs scroll loader) are handled by the caller so this
  // can be shared between scroll-mode and button-mode triggers.
  function fetchNextPage(grid, pagination, btn) {
    if (isFetching) return Promise.resolve(false);
    var url = btn && btn.dataset.nextPageUrl;
    if (!url) return Promise.resolve(false);
    isFetching = true;
    return fetch(url)
      .then(function (resp) { return resp.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('#product-grid');
        var newPagination = doc.querySelector('#eg-infinite-pagination');
        if (newGrid) {
          // Extract ONLY the <li> cards from the response's <ul>, then
          // append to the current <ul>. On the search page, #product-grid
          // is a wrapper <div> that ALSO contains the loading-overlay and
          // its own copy of #eg-infinite-pagination — appending
          // newGrid.innerHTML directly there would clone those into the
          // current grid (real duplication-of-pagination bug). getCardsUl
          // normalises both layouts.
          var newUl = getCardsUl(newGrid);
          var currentUl = getCardsUl(grid);
          if (newUl && currentUl) {
            currentUl.insertAdjacentHTML('beforeend', newUl.innerHTML);
          }
        }
        // Propagate the next-page URL from the fetched response so future
        // fetches chain correctly. Empty string = server is out of pages.
        var nextBtn = newPagination && newPagination.querySelector('.eg-pagination__button');
        btn.dataset.nextPageUrl = nextBtn && nextBtn.dataset.nextPageUrl
          ? nextBtn.dataset.nextPageUrl
          : '';
        // Bump the data-total so progress + toolbar stay accurate even if
        // the live total changed between renders (unusual but cheap).
        if (newPagination && newPagination.dataset.total) {
          pagination.dataset.total = newPagination.dataset.total;
        }
        return true;
      })
      .catch(function (err) {
        console.error('eg-infinite-pagination fetch failed', err);
        return false;
      })
      .then(function (result) {
        isFetching = false;
        return result;
      });
  }

  // Shared post-fetch state update. Decides whether to stay in scroll
  // mode, switch to button mode, or shut everything off (no more pages).
  function afterAppend(pagination, grid, btn) {
    var total = getTotal(pagination, grid);
    var revealed = Math.min(total, countVisible(grid));
    var scrollsPerCycle = getScrollsPerCycle(pagination);
    var hasMoreServer = !!(btn && btn.dataset.nextPageUrl);

    updateDisplay(pagination, grid, revealed, total);

    if (!hasMoreServer) {
      // Server is out of pages — close out, no more triggers.
      disconnectObserver();
      hideButton(pagination);
    } else if (scrollsPerCycle === 0 || scrollsThisCycle >= scrollsPerCycle) {
      // Cycle budget exhausted (or none configured for this surface) —
      // gate the next reveal behind a button click. handleButtonClick
      // resets `scrollsThisCycle` and re-arms the observer.
      disconnectObserver();
      showButton(pagination);
    }
    // else: still in scroll mode — observer stays attached.
  }

  function handleButtonClick(event) {
    var btn = event.currentTarget;
    var els = getElements();
    if (!els.grid || !els.pagination) return;
    if (isFetching) return;

    // Button-mode UI: swap label for spinner in the button itself. The
    // dedicated `.eg-pagination__loader` stays hidden — the button is
    // already a visible affordance.
    if (savedButtonHTML == null) savedButtonHTML = btn.innerHTML;
    btn.innerHTML = SPINNER_HTML;
    btn.disabled = true;

    // Snapshot the current init token so the post-fetch handler can bail
    // if a facets AJAX re-init has happened during the network round-trip.
    var token = initToken;

    fetchNextPage(els.grid, els.pagination, btn).then(function (ok) {
      btn.innerHTML = savedButtonHTML;
      btn.disabled = false;
      if (!ok) {
        btn.hidden = true;
        return;
      }
      if (initToken !== token) return;
      // Successful button-click fetch starts a fresh scroll cycle.
      scrollsThisCycle = 0;
      updateUrlAfterFetch(btn);
      var hasMoreServer = !!btn.dataset.nextPageUrl;
      var scrollsPerCycle = getScrollsPerCycle(els.pagination);
      var revealModeOn = !!els.pagination.querySelector('.eg-pagination__loader');
      // updateDisplay first so the count/progress reflect the just-appended
      // batch before any UI mode toggle below.
      var total = getTotal(els.pagination, els.grid);
      var revealed = Math.min(total, countVisible(els.grid));
      updateDisplay(els.pagination, els.grid, revealed, total);
      if (!hasMoreServer) {
        // Last page already loaded — nothing else to fetch.
        disconnectObserver();
        hideButton(els.pagination);
        return;
      }
      if (scrollsPerCycle > 0 && revealModeOn) {
        // Re-enter scroll mode for the next cycle. Hide the button and
        // re-arm the observer with skipUntilExit=true so an already-
        // visible pagination doesn't instantly chain into another fetch
        // — the user has to scroll past it once before scroll mode
        // re-engages.
        hideButton(els.pagination);
        hideLoader(els.pagination);
        attachScrollObserver(els.pagination, function () {
          if (initToken !== token) return;
          if (isFetching) return;
          showLoader(els.pagination);
          fetchNextPage(els.grid, els.pagination, btn).then(function (innerOk) {
            hideLoader(els.pagination);
            if (initToken !== token) return;
            if (!innerOk) return;
            scrollsThisCycle++;
            updateUrlAfterFetch(btn);
            afterAppend(els.pagination, els.grid, btn);
          });
        }, true);
      } else {
        // Surface doesn't opt into scroll mode (search) or cycle quota
        // is 0 — keep the button visible for the next click.
        showButton(els.pagination);
      }
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // URL persistence helpers (Variant B in the design discussion)
  //
  // We want the address bar to reflect how many pages of items the user
  // has loaded — so refresh / back-from-PDP / bookmark all restore the
  // same vertical state instead of dumping the user back to the first
  // 16 cards. Strategy:
  //   - On every successful append, `history.replaceState` updates
  //     `?page=N` where N is the highest server page the user has now
  //     fetched. Shopify's native `?page=N` semantic means "items
  //     (N-1)*step+1 .. N*step", so a fresh visit to `?page=4` server-
  //     renders ONLY page 4 (items 49-64). We then `rehydrate(4)` from
  //     the client to fetch pages 1..3 in parallel and prepend them, so
  //     the user lands on the full items 1..64 they had before reload.
  //   - `replaceState` is intentional, not `pushState`: the back button
  //     should still take the shopper to whatever they were on before
  //     the collection page, not walk them through every page they
  //     scrolled through.
  //   - `pagehide` (not `beforeunload`) saves the scroll anchor, because
  //     it fires on bfcache transitions on Safari/Firefox/Chrome where
  //     `beforeunload` doesn't. The anchor is a product id + pixel
  //     offset within that card, so the restore is robust against the
  //     items having different rendered heights between visits.
  // ────────────────────────────────────────────────────────────────────
  function getCurrentPageFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      var p = parseInt(params.get('page'), 10);
      return isFinite(p) && p > 0 ? p : 1;
    } catch (e) {
      return 1;
    }
  }

  function updateUrlAfterFetch(btn) {
    if (!btn) return;
    var nextUrl = btn.dataset.nextPageUrl;
    var loadedPage = null;
    if (nextUrl) {
      // The next-page URL we'd fetch on the next reveal — subtract 1 to
      // get the page that was just loaded.
      try {
        var u = new URL(nextUrl, window.location.origin);
        var next = parseInt(u.searchParams.get('page'), 10);
        if (isFinite(next) && next > 0) loadedPage = next - 1;
      } catch (e) {}
    } else {
      // Server is out of pages — `paginate.next` is empty, so the just-
      // loaded batch was the final one. Derive the loaded page from
      // total cards / step. Falls back to nothing if step is 0.
      var els = getElements();
      var step = els.pagination ? getStep(els.pagination) : 0;
      var total = countVisible(els.grid);
      if (step > 0 && total > 0) {
        loadedPage = Math.ceil(total / step);
      }
    }
    if (!isFinite(loadedPage) || loadedPage < 1) return;

    var u2;
    try { u2 = new URL(window.location.href); } catch (e) { return; }
    if (loadedPage === 1) {
      u2.searchParams.delete('page');
    } else {
      u2.searchParams.set('page', String(loadedPage));
    }
    var newQS = u2.search;
    var newUrl = u2.pathname + newQS + u2.hash;
    var currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (newUrl === currentUrl) return;
    try { window.history.replaceState(window.history.state, '', newUrl); } catch (e) {}
  }

  // Two-tier storage for the scroll anchor:
  //   - sessionStorage: per-tab. Restores precise position on reload.
  //   - localStorage:   per-origin. Lets a NEW tab opened on the same
  //                     URL (Cmd+Click, share-to-self, paste in another
  //                     window) restore to the same precise position
  //                     that the original tab was at. Without this,
  //                     new-tab visits fall back to "first card of
  //                     page N", which puts the user up to a full
  //                     page above where they actually were (the
  //                     "2 rows higher" merchant report).
  // sessionStorage is preferred when present — it's the per-tab truth
  // and immune to cross-tab interference (Tab A at item 73 while Tab B
  // overwrites localStorage with item 85; Tab A's reload still finds
  // sessionStorage:73 first). localStorage is the safety net for the
  // new-tab case where sessionStorage hasn't been populated yet.
  function persistScrollAnchor(data) {
    var str = JSON.stringify(data);
    try { sessionStorage.setItem(SCROLL_STORAGE_KEY, str); } catch (e) {}
    try { localStorage.setItem(SCROLL_STORAGE_KEY, str); } catch (e) {}
  }
  function readScrollAnchor() {
    var raw = null;
    try { raw = sessionStorage.getItem(SCROLL_STORAGE_KEY); } catch (e) {}
    var source = 'session';
    if (!raw) {
      try { raw = localStorage.getItem(SCROLL_STORAGE_KEY); } catch (e) {}
      source = 'local';
    }
    if (!raw) return null;
    var data;
    try { data = JSON.parse(raw); } catch (e) { return null; }
    if (!data) return null;
    data.__source = source;
    return data;
  }

  function captureScrollAnchor() {
    // Walks the grid's cards top-down and returns the first one whose
    // bottom edge is below the viewport top — i.e., the topmost visible
    // card (or the partially-visible card straddling the viewport top).
    // That's the user's perceived "you are here" item; saving its
    // productId + how-far-into-it the viewport sits gives us a stable,
    // layout-independent reference to restore against later, regardless
    // of how card heights have changed between save and restore.
    var page = getCurrentPageFromUrl();
    if (page < 2) return null;
    var els = getElements();
    if (!els.grid) return null;
    var cards = els.grid.querySelectorAll('.collection-card[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      var rect = cards[i].getBoundingClientRect();
      if (rect.bottom > 0) {
        return {
          url: window.location.pathname + window.location.search,
          productId: cards[i].dataset.productId,
          offsetWithin: -rect.top,
          ts: Date.now()
        };
      }
    }
    return null;
  }

  function saveScrollOnUnload() {
    var anchor = captureScrollAnchor();
    if (!anchor) {
      // On page=1 (or no grid), drop our SESSION entry so the next
      // pageload doesn't accidentally restore to a stale anchor.
      // localStorage is left alone — it belongs to whichever tab last
      // wrote, and the URL-mismatch check in restore prevents misuse.
      try { sessionStorage.removeItem(SCROLL_STORAGE_KEY); } catch (e) {}
      return;
    }
    persistScrollAnchor(anchor);
  }

  // Debounced scroll-driven save. Runs whenever the user pauses scrolling
  // (300ms idle), keeping localStorage current within a few hundred ms.
  // Without this, a new tab opened mid-browsing would only see whatever
  // the LAST `pagehide` wrote — typically nothing, since the source tab
  // is still alive.
  var scrollSaveTimer = null;
  function scheduleScrollSave() {
    if (scrollSaveTimer) clearTimeout(scrollSaveTimer);
    scrollSaveTimer = setTimeout(saveScrollOnUnload, 300);
  }

  function restoreScrollAfterRehydration(fallbackProductId) {
    // Two-tier restore strategy:
    //   1. Precise: persisted anchor (sessionStorage > localStorage)
    //      whose URL matches the current page — pixel-accurate scroll
    //      to where the user actually was, whether this is a refresh
    //      (sessionStorage hit) or a same-origin new-tab visit
    //      (localStorage hit).
    //   2. Fallback: scroll to the first card of the originally server-
    //      rendered page (i.e., item (N-1)*step+1). This is the path
    //      taken by fresh-browser / cross-origin visits — a shared link
    //      opened in a fresh Incognito or different device — where
    //      there's no persisted memory to draw from. Without it the
    //      user lands at the top of the prepended pages 1..N-1 and has
    //      to scroll down to find "their" page N items, defeating the
    //      point of the URL parameter.
    var data = readScrollAnchor();
    var anchorMatches = data
      && data.productId
      && data.url === window.location.pathname + window.location.search
      && (Date.now() - (data.ts || 0)) <= SCROLL_STALE_MS;

    var targetProductId = null;
    var targetOffsetWithin = 0;
    // useBlockEnd toggles between two positioning modes inside
    // performScroll:
    //   false → card.top aligns to viewport.top + offsetWithin
    //           (the precise anchor mode: where the user was before).
    //   true  → card.bottom aligns to viewport.bottom (block: 'end')
    //           — used for the fresh-visit fallback so the LAST card
    //           of page N sits at the bottom of the viewport, framing
    //           the user's view as "you've just reached the end of
    //           page N" with the pagination footer right below.
    var useBlockEnd = false;
    if (anchorMatches) {
      var anchorCard = document.querySelector('.collection-card[data-product-id="' + data.productId + '"]');
      if (anchorCard) {
        targetProductId = data.productId;
        targetOffsetWithin = data.offsetWithin || 0;
      }
      // If the saved card is gone (unpublished/sold-out filter applied
      // since save), fall through to the fresh-visit fallback below.
    }
    if (!targetProductId && fallbackProductId) {
      targetProductId = fallbackProductId;
      targetOffsetWithin = 0;
      useBlockEnd = true;
    }
    // Drop the session entry once used (it's per-tab and we don't want
    // to re-apply it on subsequent in-tab navigation). Leave localStorage
    // — other tabs may still need it; staleness TTL handles cleanup.
    try { sessionStorage.removeItem(SCROLL_STORAGE_KEY); } catch (e) {}

    if (!targetProductId) return;

    // Scroll restore strategy: lock to target card for ~1.5 seconds,
    // re-asserting once per animation frame.
    //
    // The naive `scrollTo` once approach fails on real pages because the
    // layout above our target keeps shifting after our initial scroll:
    //   - Lazy-loaded images of prepended cards finish decoding (~100–
    //     400ms each).
    //   - `font-display: swap` substitutes the web font for the system
    //     fallback once it loads (~150–800ms).
    //   - Third-party scripts (Omnisend popup, Trustpilot, Microsoft
    //     Clarity, analytics) inject DOM and recalc styles asynchronously.
    //   - Sticky elements (header, mobile drawer chrome) settle their
    //     position after the first paint.
    // Each one of those nudges target-card's `rect.top` by a few to a
    // few-dozen pixels. Without re-asserting, by the time the user looks
    // at the page they're already off by enough rows to notice ("2 rows
    // higher", per merchant report).
    //
    // The lock exits early on the FIRST sign of user-initiated scroll
    // (wheel, touch, keyboard) — otherwise we'd fight the user trying
    // to scroll away during the lock window. We don't listen for the
    // `scroll` event itself, because our own `scrollTo` calls fire it
    // too and we'd terminate ourselves. wheel/touchstart/keydown are
    // proxies for "human intent to scroll" that our DOM mutation can't
    // emit. The skip threshold (|delta|<1px) means a stable layout
    // produces zero scroll events, so there's no juddering and no
    // analytics noise.
    var lockUntil = Date.now() + 1500;
    var userExited = false;
    function exitLock() { userExited = true; }
    var exitOpts = { passive: true, once: true };
    window.addEventListener('wheel', exitLock, exitOpts);
    window.addEventListener('touchstart', exitLock, exitOpts);
    window.addEventListener('keydown', exitLock, { once: true });

    function performScroll() {
      var card = document.querySelector('.collection-card[data-product-id="' + targetProductId + '"]');
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var targetY;
      if (useBlockEnd) {
        // Align card.bottom to viewport.bottom (innerHeight). Derivation:
        // post-scroll card.top should be (innerHeight - card.height), so
        // we need to scroll by (current rect.top) - (desired top).
        targetY = window.pageYOffset + rect.top - (window.innerHeight - rect.height);
      } else {
        // Align card.top to viewport.top + offsetWithin.
        targetY = window.pageYOffset + rect.top + targetOffsetWithin;
      }
      var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (targetY < 0) targetY = 0;
      else if (targetY > maxY) targetY = maxY;
      if (Math.abs(window.pageYOffset - targetY) < 1) return; // already there
      window.scrollTo(0, targetY);
    }
    function tick() {
      if (userExited) return;
      performScroll();
      if (Date.now() < lockUntil && typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(tick);
      }
    }
    // Immediate, then start the rAF loop.
    performScroll();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(tick);
    }
    // Final safety pass for shifts that land after the rAF loop ends —
    // catches very late third-party DOM injections. Skipped if the user
    // already took over scrolling.
    setTimeout(function () { if (!userExited) performScroll(); }, 2000);
  }

  function rehydrate(targetPage, token, onDone) {
    if (rehydrating) { onDone && onDone(); return; }
    if (targetPage <= 1) { onDone && onDone(); return; }
    rehydrating = true;
    document.body.setAttribute('data-eg-rehydrating', '1');

    var els = getElements();
    if (!els.grid) {
      rehydrating = false;
      document.body.removeAttribute('data-eg-rehydrating');
      onDone && onDone();
      return;
    }

    // Take exclusive control of scroll during rehydration. Two browser
    // mechanisms can mutate scrollY during this window and produce
    // unwanted visual jumps:
    //   1. `history.scrollRestoration` (default `auto`) — already set to
    //      'manual' at script load (see `originalScrollRestoration`),
    //      but we leave the flag pinned here too in case anything reset
    //      it between then and now.
    //   2. CSS scroll-anchoring — when a 64-card fragment is prepended
    //      above the viewport, browsers may auto-shift scrollY so the
    //      previously-visible content stays stable. That's usually the
    //      right call for end-users, but it makes new-tab visits to
    //      `?page=N` race-prone: depending on whether anchoring activates
    //      before or after our explicit `scrollTo`, the user lands at a
    //      slightly different place. `overflow-anchor: none` on the grid
    //      disables anchoring for this container, leaving our scroll
    //      logic as the sole source of truth.
    if ('scrollRestoration' in window.history) {
      try { window.history.scrollRestoration = 'manual'; } catch (e) {}
    }
    var savedOverflowAnchor = els.grid.style.overflowAnchor;
    els.grid.style.overflowAnchor = 'none';

    // Capture the LAST server-rendered card BEFORE we prepend pages
    // 1..N-1. This is the final item of page N (item N*step) and serves
    // as the cross-browser fresh-visit fallback: when there's no anchor
    // in either sessionStorage or localStorage (Incognito, different
    // device, link opened after TTL expiry), restoreScrollAfterRehydration
    // scrolls so this card sits at the bottom of the viewport. The
    // intuition is that `?page=N` semantically means "I've scrolled
    // through pages 1..N", so the natural landing point is the END of
    // page N — the user sees the last few cards of page N filling the
    // viewport, with the pagination footer just below ready to load
    // page N+1.
    var serverCards = els.grid.querySelectorAll('.collection-card[data-product-id]');
    var lastServerCard = serverCards.length ? serverCards[serverCards.length - 1] : null;
    var lastServerCardId = lastServerCard ? lastServerCard.dataset.productId : null;

    var basePathname = window.location.pathname;
    // Build fetch URLs (pages 1..targetPage-1). Parallel fetch keeps
    // wall-clock time bounded to ~1× round-trip even for 6+ pages.
    var pageUrls = [];
    for (var p = 1; p < targetPage; p++) {
      var sp;
      try { sp = new URLSearchParams(window.location.search); }
      catch (e) { sp = new URLSearchParams(); }
      sp.set('page', String(p));
      pageUrls.push({ page: p, url: basePathname + '?' + sp.toString() });
    }

    Promise.all(pageUrls.map(function (entry) {
      return fetch(entry.url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) { return { page: entry.page, html: html }; })
        .catch(function (err) {
          console.warn('eg-infinite-pagination rehydration fetch failed for page', entry.page, err);
          return { page: entry.page, html: null };
        });
    })).then(function (results) {
      var finish = function () {
        rehydrating = false;
        document.body.removeAttribute('data-eg-rehydrating');
        // Restore the borrowed scroll/anchor controls so other scripts
        // and future navigation work normally. The grid's
        // `overflow-anchor` is cleared a frame later (after our own
        // scrollTo settles) so anchoring can't fire on this exact
        // mutation.
        if (originalScrollRestoration && 'scrollRestoration' in window.history) {
          try { window.history.scrollRestoration = originalScrollRestoration; } catch (e) {}
        }
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(function () {
            if (els.grid) els.grid.style.overflowAnchor = savedOverflowAnchor;
          });
        } else {
          if (els.grid) els.grid.style.overflowAnchor = savedOverflowAnchor;
        }
        onDone && onDone();
      };
      if (initToken !== token) { finish(); return; }

      // Sort by page ascending and assemble into one fragment so the
      // prepend is a single DOM mutation (one analytics observer fire,
      // one layout, one paint).
      results.sort(function (a, b) { return a.page - b.page; });
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < results.length; i++) {
        if (!results[i].html) continue;
        var doc = new DOMParser().parseFromString(results[i].html, 'text/html');
        var newGridEl = doc.querySelector('#product-grid');
        if (!newGridEl) continue;
        var newUl = getCardsUl(newGridEl);
        if (!newUl) continue;
        while (newUl.firstChild) {
          fragment.appendChild(newUl.firstChild);
        }
      }
      var currentUl = getCardsUl(els.grid);
      if (currentUl && fragment.childNodes.length) {
        currentUl.insertBefore(fragment, currentUl.firstChild);
      }

      // Sync the toolbar / progress to the new "Showing N of M" state.
      var total = getTotal(els.pagination, els.grid);
      var revealed = Math.min(total, countVisible(els.grid));
      updateDisplay(els.pagination, els.grid, revealed, total);

      // Try to scroll back to where the user was before unload; falls
      // back to the LAST card of the original page-N render on fresh
      // visits with no saved anchor (cross-browser link share).
      restoreScrollAfterRehydration(lastServerCardId);

      finish();
    });
  }

  function init() {
    var token = ++initToken;
    disconnectObserver();
    // Reset the fetching flag — an in-flight fetch from the previous DOM
    // can no longer mutate state usefully (its `btn` reference is detached
    // after facets AJAX replaces #ProductGridContainer), and leaving the
    // flag stuck-true would block future Load More clicks.
    isFetching = false;
    savedButtonHTML = null;
    // New cycle starts on every (re)init: facets AJAX resets the grid to
    // the first server page, so the user gets a fresh scroll budget.
    scrollsThisCycle = 0;

    var els = getElements();
    if (!els.grid || !els.pagination) return;

    // Deep-load rehydration: if URL says we should be at page > 1 but
    // we haven't pulled the earlier pages yet, do that first then re-
    // enter init so subsequent state (total, revealed, mode) reflects
    // the assembled grid.
    var urlPage = getCurrentPageFromUrl();
    if (urlPage > 1 && !els.grid.dataset.egRehydrated && !rehydrating) {
      els.grid.dataset.egRehydrated = '1';
      rehydrate(urlPage, token, function () {
        if (initToken !== token) return;
        init();
      });
      return;
    }

    var total = getTotal(els.pagination, els.grid);
    var revealed = Math.min(total, countVisible(els.grid));
    var scrollsPerCycle = getScrollsPerCycle(els.pagination);

    updateDisplay(els.pagination, els.grid, revealed, total);

    var btn = els.pagination.querySelector('.eg-pagination__button');
    if (btn) {
      btn.removeEventListener('click', handleButtonClick);
      btn.addEventListener('click', handleButtonClick);
    }

    var hasMoreServer = !!(btn && btn.dataset.nextPageUrl);

    // Decide mode.
    if (!hasMoreServer) {
      // Server is out of pages — nothing to fetch, nothing to show.
      hideButton(els.pagination);
      hideLoader(els.pagination);
      return;
    }

    // Reveal-mode detection. The snippet only renders the loader element
    // when `reveal_mode=true` (collection page). On the search page the
    // loader is absent, signaling button-only UX with no scroll observer
    // — preserving the original behavior of that surface.
    var revealModeOn = !!els.pagination.querySelector('.eg-pagination__loader');

    if (scrollsPerCycle === 0 || !revealModeOn) {
      // Surface doesn't opt into scroll mode (search) or cycle quota is
      // 0 — straight button-only.
      hideLoader(els.pagination);
      showButton(els.pagination);
      return;
    }

    // Sync scrollsThisCycle to the live page state when we arrived on a
    // deep URL (refresh / new-tab on `?page=N`). The alternating UX is
    // "scrolls × scrollsPerCycle, then a button click" — repeating. Each
    // page advance is one fetch, so after (N-1) fetches the user is at
    // position `(N - 1) % (scrollsPerCycle + 1)` in the cycle:
    //   page 1: pos 0   (initial, scroll mode)
    //   page 2: pos 1   (after 1 scroll-fetch, scroll mode)
    //   page 3: pos 2   (after 2 scroll-fetches → button mode)
    //   page 4: pos 0   (after button click, fresh scroll cycle)
    //   page 5: pos 1
    //   page 6: pos 2   (button mode again)
    //   page 7: pos 0
    //   …
    // Without this, `?page=3` and `?page=6` reloads would START in scroll
    // mode and auto-fetch immediately on landing — violating the
    // 2-scrolls-then-button rhythm the merchant deliberately set up.
    var wasRehydrated = false;
    var urlPageForCycle = getCurrentPageFromUrl();
    if (urlPageForCycle > 1) {
      scrollsThisCycle = (urlPageForCycle - 1) % (scrollsPerCycle + 1);
      wasRehydrated = true;
    }

    if (scrollsThisCycle >= scrollsPerCycle) {
      // Cycle exhausted — show button, no scroll observer. The next
      // click will reset `scrollsThisCycle = 0` and re-arm scroll mode.
      hideLoader(els.pagination);
      showButton(els.pagination);
      return;
    }

    // Scroll mode — observer triggers a fetch on each "user reached
    // bottom" event. Button stays hidden until the cycle quota is hit.
    //
    // skipUntilExit=true when we just rehydrated: the pagination element
    // sits near (or at) the bottom of the viewport once we've scrolled
    // to "last card of page N", and the observer's 200px rootMargin
    // would intersect it on first frame, auto-fetching page N+1 before
    // the user even sees the page. Forcing the observer to wait for one
    // exit (the user scrolling past pagination) restores the intended
    // "user pulls more in" semantics.
    hideButton(els.pagination);
    hideLoader(els.pagination);
    attachScrollObserver(els.pagination, function () {
      if (initToken !== token) return;
      if (isFetching) return;
      showLoader(els.pagination);
      fetchNextPage(els.grid, els.pagination, btn).then(function (ok) {
        hideLoader(els.pagination);
        if (initToken !== token) return;
        if (!ok) return;
        scrollsThisCycle++;
        updateUrlAfterFetch(btn);
        afterAppend(els.pagination, els.grid, btn);
      });
    }, wasRehydrated);
  }

  function watchForReplacements() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var lastGrid = container.querySelector('#product-grid');
    var debounceTimer = null;
    var ob = new MutationObserver(function () {
      var curGrid = container.querySelector('#product-grid');
      if (!curGrid) return;
      if (curGrid !== lastGrid) {
        lastGrid = curGrid;
        // Debounce — facets AJAX can fire multiple subtree mutations
        // before the new grid settles.
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(init, 30);
      }
    });
    ob.observe(container, { childList: true, subtree: true });
  }

  function watchForViewportChange() {
    // The cap differs per viewport (mobile 32, desktop 48). When the
    // mobile breakpoint crosses mid-session — phone rotation, browser
    // window resize — re-init so the new cap is applied (e.g., a desktop
    // visitor who scrolled to 32 visible should immediately get the
    // button on resize-to-mobile, instead of being able to scroll to 48).
    var mql = window.matchMedia(MOBILE_BP);
    var handler = function () { init(); };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
    } else if (typeof mql.addListener === 'function') {
      // Safari < 14 fallback.
      mql.addListener(handler);
    }
  }

  function start() {
    init();
    watchForReplacements();
    watchForViewportChange();
    // Browser back-from-PDP can restore #ProductGridContainer with a
    // different #product-grid identity that the MutationObserver may miss
    // depending on bfcache behavior — re-init defensively on popstate.
    window.addEventListener('popstate', function () {
      setTimeout(init, 200);
    });
    // Save the scroll anchor whenever the page is about to be hidden
    // (refresh, navigation, bfcache freeze). pagehide fires in all of
    // those cases where beforeunload skips bfcache transitions.
    window.addEventListener('pagehide', saveScrollOnUnload);
    // Also save while the user is actively scrolling (debounced), so a
    // new tab opened on the same URL Cmd+Click-style finds an up-to-
    // date anchor in localStorage. Without this, the source tab — still
    // alive — has only ever fired pagehide if it was previously closed,
    // leaving localStorage either empty or stale; the new tab then
    // falls back to "first card of page N" instead of the actual scroll
    // position the source tab is on.
    window.addEventListener('scroll', scheduleScrollSave, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
